import { useEffect, type ReactNode } from 'react';

import { account } from '../lib/api';
import { AutoIcon, MoonIcon, SunIcon } from './Icons';
import { LOCALES, useI18n, type Locale } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useTheme, type Theme } from '../lib/theme';

/*
 * Light, dark, or whatever the machine is doing — as three glyphs rather than
 * three words.
 *
 * Words cost this control everything it had. Each option needed its label,
 * each label needed the option to be wide enough for the longest translation
 * of it, and the whole thing needed a heading to say what the three words had
 * in common; in French that ran to "Apparence · Système Clair Sombre", which
 * did not fit and was clipped to "Systè…" and "Somb…". A sun is a sun in
 * every language and is legible at twenty-eight pixels, so the control is a
 * quarter of the height it was, cannot be broken by a longer word, and reads
 * at a glance instead of being read.
 */
const THEMES: Array<{ id: Theme; key: string; icon: ReactNode }> = [
  { id: 'system', key: 'app.theme.system', icon: <AutoIcon /> },
  { id: 'light', key: 'app.theme.light', icon: <SunIcon /> },
  { id: 'dark', key: 'app.theme.dark', icon: <MoonIcon /> },
];

/**
 * Room and language, in one row.
 *
 * Two settings, both of which change the whole app, both of which someone sets
 * once and then never again — so they are as small as two settings can be and
 * still be operated: two segmented controls side by side, no headings, no
 * labels on the options, thirty pixels tall in total where they used to be a
 * hundred and thirty.
 */
/** Whose saved settings have been applied in this page load, if anyone's. */
let appliedFor: string | undefined;

/**
 * Applies the account's saved room and language once, when someone signs in.
 *
 * On a machine this account has never used, the account's own settings are a
 * better guess than the browser's. This lives in the shell rather than in the
 * menu, and runs once per sign-in: it used to sit in the menu, which mounts
 * each time it opens, so every opening re-applied the language saved at sign-
 * in — a stale copy that never learned about a later click — and the app
 * flipped back to French under someone who had just chosen English.
 */
export function useAccountPreferences(): void {
  const { setLocale } = useI18n();
  const { setTheme } = useTheme();
  const { user } = useSession();
  const userId = user?.id;
  const savedLocale = user?.locale;
  const savedTheme = user?.theme;

  useEffect(() => {
    if (!userId || appliedFor === userId) return;
    appliedFor = userId;
    if (savedLocale === 'en' || savedLocale === 'fr') setLocale(savedLocale);
    if (savedTheme === 'dark' || savedTheme === 'light' || savedTheme === 'system') {
      setTheme(savedTheme);
    }
    // Keyed on the id alone: a later change of value is a click, already applied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
}

/**
 * The room and the language as they stand, and the two ways to change them —
 * shared by the account menu and the settings page so both save the same way.
 */
export function usePreferences() {
  const { locale, setLocale } = useI18n();
  const { theme, setTheme } = useTheme();
  const { user } = useSession();

  /*
   * Saved from the click, not from an effect watching the value: an effect
   * would write back the value the sign-in had just applied, and the two
   * would chase each other.
   */
  const chooseTheme = (next: Theme) => {
    setTheme(next);
    if (user) void account.update({ theme: next }).catch(() => undefined);
  };
  const chooseLocale = (next: Locale) => {
    setLocale(next);
    if (user) void account.update({ locale: next }).catch(() => undefined);
  };
  return { theme, locale, chooseTheme, chooseLocale };
}

export function Preferences() {
  const { t } = useI18n();
  const { theme, locale, chooseTheme, chooseLocale } = usePreferences();

  return (
    <div className="prefs">
      <div className="prefs__set prefs__set--icons" role="group" aria-label={t('app.theme')}>
        {THEMES.map((option) => (
          <button
            key={option.id}
            type="button"
            className={theme === option.id ? 'is-on' : ''}
            aria-pressed={theme === option.id}
            aria-label={t(option.key)}
            title={t(option.key)}
            onClick={() => chooseTheme(option.id)}
          >
            {option.icon}
          </button>
        ))}
      </div>

      <div className="prefs__set" role="group" aria-label={t('app.language')}>
        {LOCALES.map((option) => (
          <button
            key={option.id}
            type="button"
            className={locale === option.id ? 'is-on' : ''}
            aria-pressed={locale === option.id}
            lang={option.id}
            title={option.long}
            onClick={() => chooseLocale(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
